/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// Add local dependencies.
import { useStudioPermissions } from '../../hooks/useStudioPermissions';
import { StudioPermissions, StudioRole } from '../../types/studio';
import { hasRoleLevel } from '../../utils/permissions';

export interface ProtectedRouteProps {
  // Permission-based access control
  permission?: keyof StudioPermissions;
  permissions?: (keyof StudioPermissions)[];
  requireAll?: boolean; // If true, requires ALL permissions; if false, requires ANY

  // Role-based access control
  role?: StudioRole;
  roles?: StudioRole[];
  requireMinimumRole?: boolean; // If true, checks role hierarchy

  // Custom permission check function
  customCheck?: (
    permissions: StudioPermissions,
    role: StudioRole | null
  ) => boolean;

  // Route behavior
  children: ReactNode;
  redirectTo?: string; // Where to redirect if access denied
  fallback?: ReactNode; // Custom fallback component
  showLoadingState?: boolean; // Show loading while checking permissions

  // Error handling
  onAccessDenied?: () => void; // Callback when access is denied
}

/**
 * Component that protects routes based on user permissions
 */
export const ProtectedRoute = ({
  permission,
  permissions,
  requireAll = true,
  role,
  roles,
  requireMinimumRole = false,
  customCheck,
  children,
  redirectTo = '/dashboard',
  fallback,
  showLoadingState = true,
  onAccessDenied,
}: ProtectedRouteProps) => {
  const location = useLocation();
  const {
    permissions: userPermissions,
    role: userRole,
    hasPermission,
    isLoading,
    error,
  } = useStudioPermissions();

  // Show loading state if permissions are being fetched
  if (isLoading && showLoadingState) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking permissions...
        </Typography>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    if (fallback) {
      return fallback;
    }
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, error: 'Permission check failed' }}
        replace
      />
    );
  }

  // Check permissions
  let hasAccess = true;

  // Custom permission check takes precedence
  if (customCheck) {
    hasAccess = customCheck(userPermissions, userRole);
  } else {
    // Check specific permission
    if (permission) {
      hasAccess = hasPermission(permission);
    }

    // Check multiple permissions
    if (permissions && permissions.length > 0) {
      if (requireAll) {
        // Require ALL permissions
        hasAccess = permissions.every(perm => hasPermission(perm));
      } else {
        // Require ANY permission
        hasAccess = permissions.some(perm => hasPermission(perm));
      }
    }

    // Check role-based access
    if (role) {
      if (requireMinimumRole) {
        // Check if user has minimum role level
        hasAccess = hasAccess && hasRoleLevel(userRole, role);
      } else {
        // Check exact role match
        hasAccess = hasAccess && userRole === role;
      }
    }

    // Check multiple roles
    if (roles && roles.length > 0) {
      if (requireMinimumRole) {
        // Check if user has any of the minimum role levels
        hasAccess = hasAccess && roles.some(r => hasRoleLevel(userRole, r));
      } else {
        // Check if user has any of the exact roles
        hasAccess = hasAccess && (userRole ? roles.includes(userRole) : false);
      }
    }
  }

  // Handle access denied
  if (!hasAccess) {
    // Call access denied callback if provided
    if (onAccessDenied) {
      onAccessDenied();
    }

    // Show custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Redirect to specified route
    return (
      <Navigate
        to={redirectTo}
        state={{
          from: location,
          reason: 'insufficient_permissions',
          requiredPermission: permission,
          requiredPermissions: permissions,
          requiredRole: role,
          requiredRoles: roles,
        }}
        replace
      />
    );
  }

  // Render children if access is granted
  return children;
};

// Note: Helper components removed to comply with ESLint no-prop-spreading rule
// Use ProtectedRoute directly with appropriate props:
// - <ProtectedRoute permission="canManageMembers">...</ProtectedRoute>
// - <ProtectedRoute role={StudioRole.OWNER}>...</ProtectedRoute>
// - <ProtectedRoute role={StudioRole.MANAGER} requireMinimumRole>...</ProtectedRoute>
